import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { StatusDot, type StatusKind } from "./StatusDot";

/**
 * HazardLevel — the four-tier risk label that appears next to a node,
 * site or alert row. Always wraps a StatusDot so colour-blind users get
 * the shape, then the Korean label, then the optional numeric RI value
 * in tabular figures so vertical lists stay rail-straight.
 */

export type Level = 0 | 1 | 2 | 3;

/** 라벨은 렌더 시점에 t(labelKey) 로 현지화한다(StatusDot 과 동일 키). */
const ORDER: { kind: StatusKind; labelKey: string }[] = [
  { kind: "ok",       labelKey: "common.status.ok" },
  { kind: "caution",  labelKey: "common.status.caution" },
  { kind: "warning",  labelKey: "common.status.warning" },
  { kind: "critical", labelKey: "common.status.critical" },
];

export interface HazardLevelProps {
  level: Level;
  ri?: number;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const TEXT_SIZE: Record<NonNullable<HazardLevelProps["size"]>, string> = {
  sm: "text-[12px]",
  md: "text-[13.5px]",
  lg: "text-[15px]",
};

const DOT_SIZE: Record<NonNullable<HazardLevelProps["size"]>, number> = {
  sm: 8,
  md: 10,
  lg: 12,
};

export function HazardLevel({ level, ri, size = "md", className }: HazardLevelProps) {
  const { t } = useTranslation();
  const meta = ORDER[level];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 font-medium tracking-[-0.005em]",
        TEXT_SIZE[size],
        className,
      )}
    >
      <StatusDot kind={meta.kind} size={DOT_SIZE[size]} />
      <span>{t(meta.labelKey)}</span>
      {ri !== undefined && (
        <span className="ml-1 text-[var(--fg-subtle)] tabular-nums">
          RI {ri.toFixed(2)}
        </span>
      )}
    </span>
  );
}
